/**
 * Detects that the user edited a paragraph by watching where their cursor is,
 * for hosts without Word's paragraph events (WordApi 1.6).
 *
 * Every time Word's selection changes the detector waits for typing to settle,
 * reads the text of the paragraphs under the selection, compares each with
 * the text it saw last time, and reports which paragraph ids changed. Its one
 * follower is the document's event stream (`DocumentEvents`): the stream arms
 * it once, on a host without paragraph events, and hands each report to every
 * follower of the document, the page store behind the live proposals among
 * them. The detector remembers a bounded number of paragraphs.
 */
import { withWordContextRead } from "../../document";
import { AnchorLog } from "../anchor-log";

type EditedParagraphHandler = (uniqueLocalIds: string[]) => void;

export interface EditedParagraphDetectorConfig {
    /** Quiet time after the last selection change before the paragraphs under it are read. */
    settleMs: number;
    /** Paragraphs whose last seen text is kept; the oldest are forgotten beyond this. */
    rememberedParagraphs: number;
}

export class EditedParagraphDetector {
    private readonly lastTextById = new Map<string, string>();
    /**
     * Where an edit is reported.
     *
     * NULL
     *   Before the stream arms the detector, and again after Office refused the
     *   selection handler, so a later `follow` can arm it again.
     */
    private reportEdited: EditedParagraphHandler | null = null;
    private settleTimer: number | null = null;

    constructor(private readonly config: EditedParagraphDetectorConfig) {}

    /**
     * Arms the detector with Office's selection-change handler and reports every
     * edit it finds to `reportEdited`.
     *
     * WHEN the detector is already armed
     *   CONSEQUENCE
     *     the reports go to the new `reportEdited` and no second Office handler is
     *     registered: a second handler would read the selection twice for each move.
     */
    follow(reportEdited: EditedParagraphHandler): void {
        /** One selection move: the read waits for `settleMs` of quiet, so a burst of typing reads once. */
        const schedule = (): void => {
            /** Reads the paragraphs under the selection once the burst settles, and reports those whose text moved since the last read. */
            const fire = async (): Promise<void> => {
                /**
                 * Keeps a paragraph's latest text, the most recently seen last, and forgets
                 * the oldest beyond `rememberedParagraphs`.
                 *
                 * EXAMPLE
                 *   rememberedParagraphs = 200, a 201st paragraph is seen -> the paragraph seen longest ago is forgotten
                 */
                const rememberText = (uniqueLocalId: string, text: string): void => {
                    this.lastTextById.delete(uniqueLocalId);
                    this.lastTextById.set(uniqueLocalId, text);
                    while (this.lastTextById.size > this.config.rememberedParagraphs) {
                        const oldest = this.lastTextById.keys().next().value;
                        /**
                         * WHEN the map still exceeds rememberedParagraphs but has no oldest key
                         *   CONSEQUENCE
                         *     Break the eviction loop instead of deleting forever from an empty cache.
                         *
                         * WHY
                         *   Map iteration should yield an oldest key while entries remain. This guards the
                         *   unexpected empty case and keeps trimming from spinning.
                         */
                        if (oldest === undefined) break;
                        this.lastTextById.delete(oldest);
                    }
                };
                this.settleTimer = null;
                try {
                    const seen = await withWordContextRead(async (context) => {
                        const paragraphs = context.document.getSelection().paragraphs;
                        paragraphs.load("items/uniqueLocalId,items/text");
                        await context.sync();
                        return paragraphs.items.map((paragraph) => ({ uniqueLocalId: paragraph.uniqueLocalId, text: paragraph.text }));
                    });
                    const changedIds = seen.filter(({ uniqueLocalId, text }) => this.lastTextById.get(uniqueLocalId) !== text).map(({ uniqueLocalId }) => uniqueLocalId);
                    for (const { uniqueLocalId, text } of seen) rememberText(uniqueLocalId, text);
                    AnchorLog.record({ kind: "selection_watch", selectionIds: seen.map(({ uniqueLocalId }) => uniqueLocalId), changedIds });
                    /**
                     * WHEN no selected paragraph text differs from its last remembered text,
                     *   or Office refused the selection handler while the read was in flight
                     *   CONSEQUENCE
                     *     Report nothing.
                     *
                     * WHY
                     *   Selection moves without edits still produce a selection read, but they should
                     *   not refresh proposals.
                     */
                    if (changedIds.length === 0 || this.reportEdited === null) return;
                    this.reportEdited(changedIds);
                } catch (error) {
                    AnchorLog.record({ kind: "error", bookmarkNames: [], step: "selection watch", message: AnchorLog.messageOf(error) });
                }
            };
            /**
             * WHEN another selection change arrives before the prior settle timer fires
             *   CONSEQUENCE
             *     Cancel the prior timer so only the latest quiet period can trigger fire.
             */
            if (this.settleTimer !== null) window.clearTimeout(this.settleTimer);
            this.settleTimer = window.setTimeout(() => void fire(), this.config.settleMs);
        };
        /**
         * WHEN the detector is already armed with Office's selection-change handler
         *   CONSEQUENCE
         *     Take the new follower and do not register a second handler.
         *
         * WHY
         *   Re-arming here would duplicate callbacks for each selection change.
         */
        if (this.reportEdited !== null) {
            this.reportEdited = reportEdited;
            return;
        }
        this.reportEdited = reportEdited;
        try {
            Office.context.document.addHandlerAsync(Office.EventType.DocumentSelectionChanged, () => schedule(), (result) => {
                /**
                 * CASES
                 *   - subscription succeeded -> keep the detector armed; do nothing else
                 *   - subscription failed    -> fall through to disarm and log the Office error
                 */
                if (result.status === Office.AsyncResultStatus.Succeeded) return;
                this.reportEdited = null;
                AnchorLog.record({ kind: "error", bookmarkNames: [], step: "subscribe selection changes", message: result.error?.message ?? "unknown" });
            });
        } catch (error) {
            /**
             * WHEN there is no Office object to register with (a page outside Word, a test)
             *   CONSEQUENCE
             *     the detector stays disarmed and the caller hears the error, so the
             *     stream knows nothing reports edits.
             */
            this.reportEdited = null;
            throw error;
        }
    }
}

export const editedParagraphDetector = new EditedParagraphDetector({ settleMs: 800, rememberedParagraphs: 200 });
